import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { createRoot, type Root } from "react-dom/client";
import { MapMarker } from "./MapMarker";
import { toLibreOptions } from "@/map/utils/map-view-options";
import { DEFAULT_ZOOM, ZOOM_LIMITS } from "@/map/utils/mapConstants";
import type { CoordinateSet } from "@/map/types/map";
import type { MapCanvasProps, MapCanvasHandle } from "./map-canvas.types";

const SATELLITE_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

const GEOJSON_SOURCE = "geojson";
const LINE_LAYER = "geojson-lines";
const FILL_LAYER = "geojson-fills";
const FILL_OUTLINE_LAYER = "geojson-fill-outlines";
const OVERLAY_SOURCE = "mlOverlay";
const OVERLAY_LAYER = "mlOverlay-layer";

type ManagedMarker = {
  marker: maplibregl.Marker;
  root: Root;
  markerId: string;
  isAlert: boolean;
};

/** Decide which sets contribute clickable markers — mirrors GoogleMapCanvas. */
const isMarkerSet = (set: CoordinateSet, basicCoordinates: CoordinateSet[]) =>
  set.path.length === 1 || basicCoordinates.some((coord) => coord.name === set.name);

/** Build a GeoJSON FeatureCollection for lines (paths) + polygons. */
const buildFeatureCollection = (
  basicCoordinates: CoordinateSet[],
  polygonCoordinates: CoordinateSet[],
): GeoJSON.FeatureCollection => {
  const features: GeoJSON.Feature[] = [];

  // Multi-point basic sets → LineString (unless polyline disabled).
  for (const set of basicCoordinates) {
    if (set.path.length > 1 && !set.disablePolyline) {
      features.push({
        type: "Feature",
        properties: { name: set.name, color: set.color, kind: "line" },
        geometry: {
          type: "LineString",
          coordinates: set.path.map((p) => [p.lng, p.lat]),
        },
      });
    }
  }

  // Polygon sets → Polygon (ring auto-closed).
  for (const set of polygonCoordinates) {
    if (set.path.length < 3) continue;
    const ring = set.path.map((p) => [p.lng, p.lat] as [number, number]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
    features.push({
      type: "Feature",
      properties: { name: set.name, color: set.color, kind: "polygon" },
      geometry: { type: "Polygon", coordinates: [ring] },
    });
  }

  return { type: "FeatureCollection", features };
};

const collectBounds = (coordinateSets: CoordinateSet[]): maplibregl.LngLatBounds | null => {
  let bounds: maplibregl.LngLatBounds | null = null;
  for (const set of coordinateSets) {
    for (const point of set.path) {
      const lngLat: [number, number] = [point.lng, point.lat];
      if (!bounds) bounds = new maplibregl.LngLatBounds(lngLat, lngLat);
      else bounds.extend(lngLat);
    }
  }
  return bounds;
};

const LibreMapCanvas = forwardRef<MapCanvasHandle, MapCanvasProps>(
  (
    {
      basicCoordinates,
      polygonCoordinates,
      options,
      center,
      overlay,
      overlayEnabled,
      onMarkerClick,
      onTooltipShow,
      onTooltipHide,
      onZoomChange,
      onInBoundsChange,
      emphasizedMarkerId,
      onMarkerHover,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<maplibregl.Map | null>(null);
    const loadedRef = useRef(false);
    const markersRef = useRef<ManagedMarker[]>([]);
    const hoveredFeatureId = useRef<number | string | null>(null);

    // Keep the latest callbacks/data in refs so map event handlers (bound once)
    // always see fresh values without re-binding.
    const callbacksRef = useRef({ onTooltipShow, onTooltipHide, onZoomChange, onInBoundsChange });
    callbacksRef.current = { onTooltipShow, onTooltipHide, onZoomChange, onInBoundsChange };
    // Same for marker hover + emphasis: markers are (re)built only on data
    // changes, so their handlers and the rebuild path read these refs instead.
    const markerHoverRef = useRef(onMarkerHover);
    markerHoverRef.current = onMarkerHover;
    const emphasizedRef = useRef(emphasizedMarkerId);
    emphasizedRef.current = emphasizedMarkerId;

    /** Dim every alert marker except the emphasized one — imperative, so a
     * hover never triggers the full marker teardown/rebuild below. */
    const applyMarkerEmphasis = (emphasized: string | null | undefined) => {
      markersRef.current.forEach(({ marker, markerId, isAlert }) => {
        if (!isAlert) return;
        const el = marker.getElement();
        el.style.transition = "opacity 150ms ease";
        el.style.opacity = emphasized != null && markerId !== emphasized ? "0.5" : "";
      });
    };

    useImperativeHandle(ref, () => ({
      centerOn: (lat, lng, zoom) => {
        const map = mapRef.current;
        if (!map) return;
        const current = map.getZoom() ?? 0;
        map.flyTo({ center: [lng, lat], zoom: zoom != null ? Math.max(zoom, current) : current });
      },
      panTo: (lat, lng) => {
        mapRef.current?.panTo([lng, lat]);
      },
      isInView: (lat, lng) => {
        const bounds = mapRef.current?.getBounds();
        if (!bounds) return true;
        return bounds.contains([lng, lat]);
      },
      fitTo: (points) => {
        const map = mapRef.current;
        if (!map || points.length === 0) return;
        if (points.length === 1) {
          map.jumpTo({ center: [points[0].lng, points[0].lat], zoom: ZOOM_LIMITS.SINGLE_POINT });
          return;
        }
        const bounds = points.reduce(
          (b, p) => b.extend([p.lng, p.lat]),
          new maplibregl.LngLatBounds([points[0].lng, points[0].lat], [points[0].lng, points[0].lat]),
        );
        map.fitBounds(bounds, { padding: 48, maxZoom: ZOOM_LIMITS.MAX_ZOOM, animate: false });
      },
    }));

    // --- Init map (once) -----------------------------------------------------
    useEffect(() => {
      if (!containerRef.current) return;
      const o = toLibreOptions(options);
      const map = new maplibregl.Map({
        container: containerRef.current,
        center: [center.lng, center.lat],
        zoom: o.zoom,
        minZoom: o.minZoom,
        maxZoom: o.maxZoom,
        attributionControl: false,
        style: {
          version: 8,
          sources: {
            satellite: {
              type: "raster",
              tiles: [SATELLITE_TILES],
              tileSize: 256,
              maxzoom: 19,
              attribution: "Esri, Maxar, Earthstar Geographics",
            },
          },
          layers: [{ id: "satellite", type: "raster", source: "satellite" }],
        },
      });
      mapRef.current = map;

      if (o.zoomControl) {
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      }

      map.on("zoom", () => callbacksRef.current.onZoomChange?.(map.getZoom()));
      map.on("moveend", () => callbacksRef.current.onInBoundsChange?.(true));

      map.on("load", () => {
        loadedRef.current = true;

        // GeoJSON source + layers for paths/polygons.
        map.addSource(GEOJSON_SOURCE, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
          generateId: true,
        });

        map.addLayer({
          id: FILL_LAYER,
          type: "fill",
          source: GEOJSON_SOURCE,
          filter: ["==", ["get", "kind"], "polygon"],
          paint: {
            "fill-color": ["get", "color"],
            "fill-opacity": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              0.4,
              0.2,
            ],
          },
        });

        map.addLayer({
          id: FILL_OUTLINE_LAYER,
          type: "line",
          source: GEOJSON_SOURCE,
          filter: ["==", ["get", "kind"], "polygon"],
          paint: {
            "line-color": ["get", "color"],
            "line-width": 2,
            "line-opacity": 0.8,
          },
        });

        map.addLayer({
          id: LINE_LAYER,
          type: "line",
          source: GEOJSON_SOURCE,
          filter: ["==", ["get", "kind"], "line"],
          layout: { "line-cap": "round", "line-join": "round" },
          paint: {
            "line-color": ["get", "color"],
            "line-width": 2,
            "line-opacity": 0.8,
          },
        });

        const handleMove = (
          e: maplibregl.MapLayerMouseEvent,
          opts?: { setHover?: boolean },
        ) => {
          map.getCanvas().style.cursor = "pointer";
          const feature = e.features?.[0];
          if (!feature) return;
          if (opts?.setHover) {
            if (hoveredFeatureId.current != null) {
              map.setFeatureState(
                { source: GEOJSON_SOURCE, id: hoveredFeatureId.current },
                { hover: false },
              );
            }
            if (feature.id != null) {
              hoveredFeatureId.current = feature.id;
              map.setFeatureState(
                { source: GEOJSON_SOURCE, id: feature.id },
                { hover: true },
              );
            }
          }
          const props = feature.properties ?? {};
          callbacksRef.current.onTooltipShow({
            name: String(props.name ?? ""),
            x: e.point.x,
            y: e.point.y,
            color: props.color ? String(props.color) : undefined,
          });
        };

        const handleLeave = (opts?: { clearHover?: boolean }) => {
          map.getCanvas().style.cursor = "";
          if (opts?.clearHover && hoveredFeatureId.current != null) {
            map.setFeatureState(
              { source: GEOJSON_SOURCE, id: hoveredFeatureId.current },
              { hover: false },
            );
            hoveredFeatureId.current = null;
          }
          callbacksRef.current.onTooltipHide();
        };

        map.on("mousemove", LINE_LAYER, (e) => handleMove(e));
        map.on("mouseleave", LINE_LAYER, () => handleLeave());
        map.on("mousemove", FILL_LAYER, (e) => handleMove(e, { setHover: true }));
        map.on("mouseleave", FILL_LAYER, () => handleLeave({ clearHover: true }));

        // Trigger the data/marker/overlay/bounds effects now that the style is ready.
        map.fire("libre-ready");
      });

      return () => {
        loadedRef.current = false;
        markersRef.current.forEach(({ marker, root }) => {
          marker.remove();
          // Defer unmount to avoid React "synchronous unmount during render" warning.
          queueMicrotask(() => root.unmount());
        });
        markersRef.current = [];
        map.remove();
        mapRef.current = null;
      };
      // Init once; subsequent prop changes handled by the effects below.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Paths + polygons data ----------------------------------------------
    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;
      const apply = () => {
        const source = map.getSource(GEOJSON_SOURCE) as maplibregl.GeoJSONSource | undefined;
        if (!source) return;
        source.setData(buildFeatureCollection(basicCoordinates, polygonCoordinates));
      };
      if (loadedRef.current) apply();
      else map.once("libre-ready", apply);
    }, [basicCoordinates, polygonCoordinates]);

    // --- Markers -------------------------------------------------------------
    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;

      const apply = () => {
        // Tear down previous markers.
        markersRef.current.forEach(({ marker, root }) => {
          marker.remove();
          queueMicrotask(() => root.unmount());
        });
        markersRef.current = [];

        const allCoordinates = [...basicCoordinates, ...polygonCoordinates];
        for (const set of allCoordinates) {
          if (!isMarkerSet(set, basicCoordinates)) continue;
          const { name, path, color, isActive, disableShadow, markerType, isReviewed, isSelected, selectionId, zIndex } = set;
          path.forEach((point, idx) => {
            const markerId = selectionId ?? `${name}-${idx}`;
            const isAlert = markerType === "alert";
            const el = document.createElement("div");
            // Marker visuals use absolute positioning relative to this anchor box.
            el.style.position = "relative";
            el.style.width = "0px";
            el.style.height = "0px";
            // Stacking: maplibre positions markers via transform, which makes
            // each one its own stacking context — order them on the wrapper.
            if (zIndex != null) el.style.zIndex = String(zIndex);

            const root = createRoot(el);
            root.render(
              <MapMarker
                lat={point.lat}
                lng={point.lng}
                markerId={markerId}
                label={name}
                onClick={() =>
                  onMarkerClick({
                    markerId,
                    lat: point.lat,
                    lng: point.lng,
                    time: point.time ? new Date(point.time) : undefined,
                  })
                }
                color={color}
                isActive={isActive}
                isLast={idx === path.length - 1}
                time={point.time}
                disableShadow={disableShadow}
                count={point.count}
                markerType={markerType}
                isReviewed={isReviewed}
                isSelected={isSelected}
                onHover={
                  isAlert ? (id, hovering) => markerHoverRef.current?.(id, hovering) : undefined
                }
              />,
            );

            const marker = new maplibregl.Marker({ element: el, anchor: "center" })
              .setLngLat([point.lng, point.lat])
              .addTo(map);
            markersRef.current.push({ marker, root, markerId, isAlert });
          });
        }

        // A rebuild wipes inline styles — re-apply the current emphasis.
        applyMarkerEmphasis(emphasizedRef.current);
      };

      if (loadedRef.current) apply();
      else map.once("libre-ready", apply);
      // onMarkerClick is stable enough (defined in shell render) — re-run on coords.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basicCoordinates, polygonCoordinates]);

    // --- Alert emphasis (hover/selection dim) --------------------------------
    useEffect(() => {
      applyMarkerEmphasis(emphasizedMarkerId);
    }, [emphasizedMarkerId]);

    // --- ML tile overlay -----------------------------------------------------
    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;

      const removeOverlay = () => {
        if (map.getLayer(OVERLAY_LAYER)) map.removeLayer(OVERLAY_LAYER);
        if (map.getSource(OVERLAY_SOURCE)) map.removeSource(OVERLAY_SOURCE);
      };

      const apply = () => {
        removeOverlay();
        if (!overlay || !overlayEnabled) return;
        map.addSource(OVERLAY_SOURCE, {
          type: "raster",
          tiles: [overlay.tile_url_template],
          scheme: overlay.scheme === "tms" ? "tms" : "xyz",
          minzoom: overlay.min_zoom,
          maxzoom: overlay.max_zoom,
          tileSize: 256,
        });
        // Insert above the satellite/geojson layers but below markers (DOM).
        map.addLayer({
          id: OVERLAY_LAYER,
          type: "raster",
          source: OVERLAY_SOURCE,
          paint: { "raster-opacity": overlay.opacity },
        });
      };

      if (loadedRef.current) apply();
      else map.once("libre-ready", apply);

      return () => {
        if (loadedRef.current && mapRef.current) removeOverlay();
      };
    }, [overlay, overlayEnabled]);

    // --- Fit bounds on data change ------------------------------------------
    useEffect(() => {
      const map = mapRef.current;
      if (!map) return;

      const apply = () => {
        const allCoordinates = [...basicCoordinates, ...polygonCoordinates];
        const totalPoints = allCoordinates.reduce((sum, set) => sum + set.path.length, 0);
        if (totalPoints === 0) return;

        if (totalPoints === 1) {
          const point = allCoordinates.find((s) => s.path.length > 0)?.path[0];
          if (point) map.flyTo({ center: [point.lng, point.lat], zoom: options.defaultZoom ?? DEFAULT_ZOOM });
          return;
        }

        const bounds = collectBounds(allCoordinates);
        if (bounds) map.fitBounds(bounds, { padding: 60, maxZoom: 18, duration: 0 });
      };

      if (loadedRef.current) apply();
      else map.once("libre-ready", apply);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basicCoordinates, polygonCoordinates]);

    // --- Resize observer -----------------------------------------------------
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const observer = new ResizeObserver(() => mapRef.current?.resize());
      observer.observe(container);
      return () => observer.disconnect();
    }, []);

    return (
      <div
        ref={containerRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
    );
  },
);
LibreMapCanvas.displayName = "LibreMapCanvas";
export default LibreMapCanvas;
